/*
  Choose options on a product card (snippets/product-card-purchase.liquid).

  The button opens the card's <dialog> as a small sheet listing each
  variant with its price: centred on larger screens, a bottom sheet on
  phones (drag it down to close). While open the dialog is moved to <body>,
  so no card, shelf or section style or handler reaches it; it goes back
  into the card when it closes. Adding goes through the theme's own
  product-form, and once the add lands the sheet steps aside for the cart
  drawer.
*/
(() => {
  if (customElements.get('card-options')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canUseDialog = typeof HTMLDialogElement === 'function' && 'showModal' in HTMLDialogElement.prototype;

  class CardOptions extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;

      this.opener = this.querySelector('[data-card-options-open]');
      this.dialog = this.querySelector('dialog');
      if (!this.opener || !this.dialog) return;

      this.panel = this.dialog.querySelector('.card-options__panel');
      this.form = this.dialog.querySelector('form');

      this.opener.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(event.detail === 0);
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      // a press that starts and ends on the dim area around the panel
      this.dialog.addEventListener('pointerdown', (event) => {
        this.pressedOutside = event.target === this.dialog;
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && this.pressedOutside) this.close();
        this.pressedOutside = false;
      });

      this.dialog.querySelectorAll('[data-card-options-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      // captured on the dialog, so this runs before product-form's own
      // submit handler marks the button busy
      this.dialog.addEventListener('submit', (event) => {
        if (event.target === this.form && !this.form.querySelector('[type="submit"]')?.hasAttribute('aria-disabled')) this.adding = true;
      }, true);

      this.onAdded = () => {
        if (!this.adding) return;
        this.adding = false;
        this.close({ returnFocus: false });
      };
      this.onError = () => {
        this.adding = false;
      };

      this.setUpDrag();
    }

    disconnectedCallback() {
      // the card was re-rendered while its sheet was open
      if (this.dialog?.open && !this.contains(this.dialog)) {
        this.finishClose();
        this.dialog.remove();
      }
    }

    open(fromKeyboard = false) {
      if (!this.dialog || this.dialog.open) return;
      if (!canUseDialog) {
        if (this.opener.dataset.href) window.location.href = this.opener.dataset.href;
        return;
      }

      clearTimeout(this.closeTimer);
      this.closing = false;
      document.body.appendChild(this.dialog);
      this.dialog.showModal();
      this.dialog.classList.remove('is-open', 'is-closing');
      this.opener.setAttribute('aria-expanded', 'true');
      window.theme?.scrollLock?.lock(this);
      document.addEventListener('ajaxProduct:added', this.onAdded);
      document.addEventListener('ajaxProduct:error', this.onError);

      const checked = this.dialog.querySelector('input[name="id"]:checked') || this.dialog.querySelector('input[name="id"]:not(:disabled)');
      if (fromKeyboard && checked) {
        checked.focus({ preventScroll: true });
      } else {
        this.panel.setAttribute('tabindex', '-1');
        this.panel.focus({ preventScroll: true });
      }

      // two frames, so the closed look is drawn before it eases in
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.dialog.open && !this.closing) this.dialog.classList.add('is-open');
      }));
    }

    close({ returnFocus = true } = {}) {
      if (!this.dialog?.open || this.closing) return;
      this.closing = true;
      this.returnFocus = returnFocus;
      this.dialog.classList.add('is-closing');
      this.dialog.classList.remove('is-open');
      this.panel.classList.remove('is-dragging');
      window.theme?.scrollLock?.unlock(this);

      const wait = reducedMotion() ? 0 : 320;
      this.closeTimer = setTimeout(() => this.finishClose(), wait);
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      window.theme?.scrollLock?.unlock(this);
      document.removeEventListener('ajaxProduct:added', this.onAdded);
      document.removeEventListener('ajaxProduct:error', this.onError);
      this.dialog.classList.remove('is-open', 'is-closing');
      this.panel.style.removeProperty('--drag');
      if (this.dialog.open) this.dialog.close();
      this.closing = false;
      this.opener.setAttribute('aria-expanded', 'false');
      if (this.isConnected) {
        this.appendChild(this.dialog);
        if (this.returnFocus !== false) this.opener.focus({ preventScroll: true });
      }
      this.returnFocus = true;
    }

    // phones: pull the sheet down by its top to close it
    setUpDrag() {
      const handle = this.dialog.querySelector('.card-options__head');
      if (!handle) return;
      let startY = 0;
      let lastY = 0;
      let lastTime = 0;
      let speed = 0;
      let dragging = false;

      handle.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.target.closest('button, a')) return;
        if (!window.matchMedia('(max-width: 639px)').matches) return;
        dragging = true;
        startY = lastY = event.clientY;
        lastTime = event.timeStamp;
        speed = 0;
        handle.setPointerCapture(event.pointerId);
        this.panel.classList.add('is-dragging');
      });

      handle.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const offset = Math.max(0, event.clientY - startY);
        const elapsed = event.timeStamp - lastTime;
        if (elapsed > 0) speed = (event.clientY - lastY) / elapsed;
        lastY = event.clientY;
        lastTime = event.timeStamp;
        this.panel.style.setProperty('--drag', `${offset}px`);
      });

      const release = (event) => {
        if (!dragging) return;
        dragging = false;
        const offset = Math.max(0, event.clientY - startY);
        this.panel.classList.remove('is-dragging');
        if (offset > Math.min(140, this.panel.offsetHeight * 0.3) || speed > 0.6) {
          this.close();
        } else {
          this.panel.style.removeProperty('--drag');
        }
      };
      handle.addEventListener('pointerup', release);
      handle.addEventListener('pointercancel', release);
    }
  }

  customElements.define('card-options', CardOptions);
})();
